import React, { useEffect, useRef, useState } from 'react';
import API from '../../../api/axiosInstance';
import OnsiteWaveformPlayer from './OnsiteWaveformPlayer';
import OnsiteAudioCropper from './OnsiteAudioCropper';
import useAudioRecorder from './useAudioRecorder';
// import useAudioFilter from './useAudioFilter'; // DISABLED — filter library not stable; re-enable when optimised
import { formatTime } from './audioUtils';

const btnCircle = {
  width: '44px', height: '44px', borderRadius: '50%', background: '#c0392b', border: 'none',
  color: '#fff', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center',
  justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(192,57,43,0.3)',
};

const reRecordBtnStyle = (hovered) => ({
  background: hovered ? '#FBF3DC' : 'none',
  border: `1px solid ${hovered ? '#BFA253' : '#ddd'}`,
  borderRadius: '8px', padding: '6px 14px', fontSize: '12px',
  color: hovered ? '#8a6d00' : '#777', cursor: 'pointer',
  fontFamily: '"Poppins", sans-serif', flexShrink: 0,
  transition: 'all 0.15s',
});

const container = {
  background: '#f9f7f2', borderRadius: '12px', padding: '16px 20px',
  display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
};

// Props:
//   existingUrl   — S3 URL already saved in DB (loaded on form open)
//   onBlobReady   — called with blob when recording stops (null when cleared)
//   onDeleteUrl   — called when existing S3 recording is deleted
const OnsiteAudioRecorder = ({ className, existingUrl, onBlobReady, onDeleteUrl }) => {
  const [status, setStatus] = useState('idle'); // idle | recording | recorded | loading | uploaded | error
  const [audioUrl, setAudioUrl] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [hover1, setHover1] = useState(false);
  const [hover2, setHover2] = useState(false);
  const cropSourceRef = useRef(null); // 'recorded' | 'uploaded'

  const { elapsed, level, error, localUrlRef, cleanup, startRecording, stopRecording } =
    useAudioRecorder({ onBlobReady });
  // const { filterState, filterAudio, resetFilter } = useAudioFilter(localUrlRef, setAudioUrl); // DISABLED

  // Load existing S3 recording via backend proxy (CORS-safe blob URL for waveform extraction)
  useEffect(() => {
    if (!existingUrl) return;
    setStatus('loading');
    API.get('/ems/uploads/audio/proxy', { params: { url: existingUrl }, responseType: 'blob' })
      .then((res) => {
        if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
        const blobUrl = URL.createObjectURL(res.data);
        localUrlRef.current = blobUrl;
        setAudioUrl(blobUrl);
        setStatus('uploaded');
      })
      .catch((err) => {
        console.error('[AudioProxy] failed:', err?.response?.status, err?.message);
        setAudioUrl(existingUrl);
        setStatus('uploaded');
      });
  }, [existingUrl]);

  const reRecord = () => {
    // resetFilter(); // DISABLED with audio filter feature
    cleanup();
    setAudioUrl('');
    setStatus('idle');
    onBlobReady?.(null);
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);
    // Use existingUrl (S3 URL from prop) not audioUrl (blob URL) — blob URL fails key extraction
    try { await API.delete('/ems/uploads/audio', { data: { url: existingUrl } }); } catch { /* ignore */ }
    onDeleteUrl?.();
    reRecord();
  };

  const openCropper = (source) => {
    cropSourceRef.current = source;
    setShowCropper(true);
  };

  const handleCropDone = async (croppedBlob) => {
    const newUrl = URL.createObjectURL(croppedBlob);

    if (cropSourceRef.current === 'uploaded') {
      try { await API.delete('/ems/uploads/audio', { data: { url: existingUrl } }); } catch { /* ignore */ }
      onDeleteUrl?.();
      onBlobReady?.(croppedBlob);
      localUrlRef.current = newUrl;
      setAudioUrl(newUrl);
      setStatus('recorded');
    } else {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      localUrlRef.current = newUrl;
      setAudioUrl(newUrl);
      onBlobReady?.(croppedBlob);
    }

    setShowCropper(false);
  };

  return (
    <div className={className}>
      <div style={container}>

        {/* IDLE */}
        {status === 'idle' && (
          <>
            <button type="button" onClick={() => startRecording({
              onStart: () => setStatus('recording'),
              onStop: (url) => { setAudioUrl(url); setStatus('recorded'); },
              onError: () => setStatus('error'),
            })} style={btnCircle}>
              <i className="fa-solid fa-microphone"></i>
            </button>
            <span style={{ fontSize: '13px', color: '#999' }}>Press to record pronunciation</span>
          </>
        )}

        {/* RECORDING */}
        {status === 'recording' && (
          <>
            <button type="button" onClick={stopRecording} style={{ ...btnCircle, animation: 'onsite-pulse 1.5s infinite' }}>
              <i className="fa-solid fa-stop"></i>
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#c0392b' }}>
                Recording… {formatTime(elapsed)}
                {elapsed >= 25 && <span style={{ fontSize: '11px', marginLeft: '8px', opacity: 0.8 }}>auto-stop at 0:30</span>}
              </div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>Speak the participant's name clearly</div>
              <div style={{ marginTop: '8px', height: '4px', background: '#e0ddd6', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${level * 100}%`, background: level > 0.7 ? '#c0392b' : '#BFA253', transition: 'width 0.1s' }} />
              </div>
            </div>
          </>
        )}

        {/* RECORDED — blob ready, will upload on form submit */}
        {status === 'recorded' && (
          <>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#BFA253', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="fa-solid fa-check" style={{ color: '#fff', fontSize: '16px' }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#2c2c2c' }}>
                Recorded — will save on registration
              </div>
              {audioUrl && (
                <div style={{ marginTop: 6 }}>
                  <OnsiteWaveformPlayer src={audioUrl} />
                  {/* DISABLED — audio filter button hidden; useAudioFilter library unstable, needs optimisation
                    {filterState === 'idle' && (
                      <button type="button" onClick={filterAudio} ...>Filter Audio</button>
                    )}
                    {filterState === 'processing' && <span>Filtering…</span>}
                  */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 6 }}>
                    <div style={{ flex: 1 }} />
                    <button type="button" onClick={() => openCropper('recorded')} style={reRecordBtnStyle(hover1)} onMouseEnter={() => setHover1(true)} onMouseLeave={() => setHover1(false)}>
                      <i className="fa-solid fa-scissors" style={{ marginRight: '5px', fontSize: '11px' }}></i>Crop
                    </button>
                    <button type="button" onClick={reRecord} style={reRecordBtnStyle(false)}>
                      Re-record
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* LOADING — proxy fetch in progress */}
        {status === 'loading' && (
          <>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#00573C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ color: '#fff', fontSize: '16px' }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#00573C' }}>Pronunciation recorded</div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>Loading…</div>
            </div>
          </>
        )}

        {/* UPLOADED — existing S3 recording loaded from DB */}
        {status === 'uploaded' && (
          <>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#00573C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="fa-solid fa-check" style={{ color: '#fff', fontSize: '16px' }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#00573C' }}>Pronunciation recorded</div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>IOC / external capture confirmed</div>
              {audioUrl && (
                <div style={{ marginTop: 6 }}>
                  <OnsiteWaveformPlayer src={audioUrl} />
                  <div style={{ display: 'flex', gap: '8px', marginTop: 6, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => openCropper('uploaded')} style={reRecordBtnStyle(hover2)} onMouseEnter={() => setHover2(true)} onMouseLeave={() => setHover2(false)}>
                      <i className="fa-solid fa-scissors" style={{ marginRight: '5px', fontSize: '11px' }}></i>Crop
                    </button>
                    <button type="button" onClick={reRecord} style={reRecordBtnStyle(false)}>
                      Re-record
                    </button>
                    <button type="button" onClick={() => setShowDeleteModal(true)} style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '8px', padding: '6px 14px', fontSize: '12px', color: '#c0392b', cursor: 'pointer', fontFamily: '"Poppins", sans-serif' }}>
                      <i className="fa-solid fa-trash" style={{ marginRight: '5px' }}></i>Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ERROR */}
        {status === 'error' && (
          <>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#fdecea', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="fa-solid fa-microphone-slash" style={{ color: '#c0392b', fontSize: '16px' }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', color: '#c0392b' }}>{error}</div>
            </div>
            <button type="button" onClick={() => startRecording({
              onStart: () => setStatus('recording'),
              onStop: (url) => { setAudioUrl(url); setStatus('recorded'); },
              onError: () => setStatus('error'),
            })} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '6px 14px', fontSize: '12px', color: '#777', cursor: 'pointer', fontFamily: '"Poppins", sans-serif' }}>
              Try again
            </button>
          </>
        )}

      </div>

      <style>{`@keyframes onsite-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(192,57,43,0.4)} 50%{box-shadow:0 0 0 10px rgba(192,57,43,0)} }`}</style>

      {/* Crop modal */}
      {showCropper && audioUrl && (
        <OnsiteAudioCropper
          src={audioUrl}
          onCropDone={handleCropDone}
          onCancel={() => setShowCropper(false)}
        />
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px 28px', maxWidth: '380px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', fontFamily: '"Poppins", sans-serif' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#fdecea', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <i className="fa-solid fa-trash" style={{ color: '#c0392b', fontSize: '20px' }}></i>
            </div>
            <h4 style={{ textAlign: 'center', fontSize: '17px', fontWeight: 700, color: '#2c2c2c', margin: '0 0 8px' }}>Delete Recording?</h4>
            <p style={{ textAlign: 'center', fontSize: '13px', color: '#888', margin: '0 0 24px' }}>This recording will be permanently deleted and cannot be undone.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setShowDeleteModal(false)} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1.5px solid #e4e0d8', background: '#fff', fontSize: '14px', fontWeight: 600, color: '#555', cursor: 'pointer', fontFamily: '"Poppins", sans-serif' }}>
                Cancel
              </button>
              <button type="button" onClick={confirmDelete} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', background: '#c0392b', fontSize: '14px', fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: '"Poppins", sans-serif' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnsiteAudioRecorder;
